import { useState, type ReactElement } from 'react';
import { ORDNER_MAX, gleicherOrdner, ordnerName, ordnerVorschlag } from '@schnitt/shared';
import type { ProjectSummary } from '../api';
import { Sheet } from './Sheet';

/**
 * **Einen Ordner auf der Startseite anlegen** (7.158).
 *
 * Sascha, 24. September: „Kann ich auf der Startseite keine Ordner anlegen?
 * Irgendwie geht das nur beim Projekt anlegen. Das ist ja aber falsch. Man hat
 * ja seine eigene Organisationsstruktur. Aber das muss man halt organisieren
 * können.“
 *
 * Ein Ordner ist hier die Gruppe seiner Projekte -- er steht an jedem Projekt
 * und nirgends sonst. Deshalb fragt dieses Blatt beides auf einmal: wie er
 * heisst und was hineingehört. Ein Ordner ohne Projekt wäre ein Name, der
 * nirgends steht, und verschwände beim nächsten Laden wieder.
 */
export function OrdnerNeuSheet({
  projekte,
  vorhandene,
  laeuft,
  onClose,
  onFertig,
}: {
  projekte: ProjectSummary[];
  vorhandene: string[];
  laeuft: boolean;
  onClose: () => void;
  onFertig: (name: string, ids: string[]) => void | Promise<void>;
}): ReactElement {
  const [name, setName] = useState('');
  const [gewaehlt, setGewaehlt] = useState<string[]>([]);
  const sauber = ordnerName(name);
  const vorschlag = sauber ? ordnerVorschlag(sauber, vorhandene) : null;
  const gibtEsSchon = vorhandene.find((f) => gleicherOrdner(f, sauber)) ?? null;
  const umschalten = (id: string) =>
    setGewaehlt((g) => (g.includes(id) ? g.filter((x) => x !== id) : [...g, id]));

  return (
    <Sheet title="Neuer Ordner" onClose={onClose} closeLabel="Abbrechen">
      <label className="field">
        <span>Name</span>
        <input
          value={name}
          autoFocus
          maxLength={ORDNER_MAX}
          placeholder="z. B. Kunden, 2026, Kunde / Messe"
          data-ordner-neu-name
          onChange={(e) => setName(e.target.value)}
        />
      </label>
      {/* Unterordner (S47): ein Schrägstrich, und der Ordner liegt im anderen. */}
      {!sauber ? (
        <p className="hint" style={{ marginTop: -6 }} data-ordner-unter-hinweis>
          Mit „/“ wird es ein Unterordner: „Kunde / Messe“.
        </p>
      ) : null}
      {gibtEsSchon ? (
        <p className="hint" style={{ marginTop: -6 }} data-ordner-gibt-es>
          „{gibtEsSchon}“ gibt es schon — die gewählten Projekte kommen dazu.
        </p>
      ) : vorschlag ? (
        <button className="row" style={{ marginTop: -6 }} data-ordner-vorschlag onClick={() => setName(vorschlag)}>
          Meinst du „{vorschlag}“?
        </button>
      ) : null}

      <div className="field">
        <span>Was gehört hinein?</span>
        <div className="list" data-ordner-projekte>
          {projekte.map((p) => {
            const drin = gewaehlt.includes(p.id);
            return (
              <button
                key={p.id}
                className={`row ordner-projekt ${drin ? 'active' : ''}`}
                data-ordner-projekt={p.id}
                aria-pressed={drin}
                onClick={() => umschalten(p.id)}
              >
                <span className="haken" aria-hidden="true">{drin ? '✓' : ''}</span>
                <span className="grow" style={{ minWidth: 0, textAlign: 'left' }}>
                  <span className="title">{p.name}</span>
                  <span className="sub" style={{ display: 'block' }}>{p.folder ? `jetzt in „${p.folder}“` : 'ohne Ordner'}</span>
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <button
        className="primary block"
        disabled={!sauber || gewaehlt.length === 0 || laeuft}
        data-ordner-neu-anlegen
        onClick={() => void onFertig(gibtEsSchon ?? sauber, gewaehlt)}
      >
        {laeuft
          ? 'Lege an…'
          : gewaehlt.length === 0
            ? 'Anlegen'
            : `Anlegen · ${gewaehlt.length === 1 ? '1 Projekt' : `${gewaehlt.length} Projekte`}`}
      </button>
      <p className="hint">
        Ein Ordner ist die Gruppe seiner Projekte. Wird er leer, verschwindet er wieder.
      </p>
    </Sheet>
  );
}
